import axios from "axios";
import { amRoutes, ucEndpointsUrls } from "../../config/config";
import { AppDispatch, RootState } from "../../reducers";
import { history } from "../../store";
import { hideSuccessMessage, showSuccessMessage } from "../../store/statusMessages";
import {
  addSubscriptionCheckoutFailed,
  getCurrentPencilerSubscriptionFailed,
  getCurrentPencilerSubscriptionSuccess,
  getProrationCostFailed,
  getProrationCostSuccess,
  getSubscriptionOptionsFailed,
  getSubscriptionOptionsSuccess,
  gettingCurrentPencilerSubscription,
  gettingProrationCost,
  gettingSubscriptionOptions,
  setUpgradeSubscriptionModalStatus,
  submittingUpgradeSubscription,
  submitUpgradeSubscriptionFailed,
  submitUpgradeSubscriptionSuccess,
  upgradeSubscriptionCheckoutFailed
} from "../../store/subscriptions";
import { addStripeSubscription } from "../../utils/stripe";
import { delay, getErrorMessageFromRequest } from "../../utils/utils";
import { getPencilerStartProjectsStatus, refreshOrganization } from "../accounts/accounts";

/**
 * Get Subscription Options and populates Redux State.
 */
export const getSubscriptionOptions = () => (dispatch: AppDispatch) => {
  dispatch(gettingSubscriptionOptions());
  axios
    .get(ucEndpointsUrls.subscriptionOptions, {
      params: {
        sort: { field: "title", order: "ASC", type: "numericString" }
      }
    })
    .then(res => dispatch(getSubscriptionOptionsSuccess(res.data)))
    .catch(error => dispatch(getSubscriptionOptionsFailed(getErrorMessageFromRequest(error))));
};

/**
 * Redirects Add Subscription to Stripe Checkout.
 *
 * @param {String} subscriptionStripeId       - Stripe subscriptions ID.
 */
export const addSubscriptionCheckoutRedirect = (subscriptionStripeId: string) => (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const userData = getState().users.userClaims;
  // @ts-ignore: userData arguments are unused
  addStripeSubscription(subscriptionStripeId, userData.email, userData.userId).catch(error =>
    dispatch(addSubscriptionCheckoutFailed(getErrorMessageFromRequest(error)))
  );
};

/**
 * Redirects Upgrade Subscription to Stripe Checkout.
 *
 * @param {String} subscriptionStripeId       - Stripe subscriptions ID.
 * @return {Function}
 */
export const upgradeSubscriptionCheckoutRedirect = (subscriptionStripeId: string) => (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const userData = getState().users.userClaims;
  // @ts-ignore: userData arguments are unused
  addStripeSubscription(subscriptionStripeId, userData.email, userData.userId).catch(error =>
    dispatch(upgradeSubscriptionCheckoutFailed(getErrorMessageFromRequest(error)))
  );
};

/**
 * Submit upgrade Stripe penciler subscription.
 * (UC's api handles subscription upgrades / proration).
 *
 * @param {String} newPlanId        - Plan ID to be upgraded to.
 * @param {String | null} prorationToken   - Proration token.
 * @return {}                       - Redux async function.
 */
export const upgradeStripePencilerSubscription = (
  newPlanId: string,
  prorationToken: string | null
) => (dispatch: AppDispatch) => {
  dispatch(submittingUpgradeSubscription());
  axios
    .put(ucEndpointsUrls.stripeProrationCost.replace(":planId", newPlanId), {
      prorationToken: prorationToken
    })
    .then(() => {
      dispatch(submitUpgradeSubscriptionSuccess());
      dispatch(getPencilerStartProjectsStatus());
      dispatch(setUpgradeSubscriptionModalStatus(false));
      dispatch(refreshOrganization());
      dispatch(showSuccessMessage("Subscription successfully upgraded!", "Redirecting..."));
      return delay(2000);
    })
    .then(() => {
      dispatch(hideSuccessMessage());
      history.push(amRoutes.managementSubscription);
    })
    .catch(error => dispatch(submitUpgradeSubscriptionFailed(getErrorMessageFromRequest(error))));
};

/**
 * Get proration cost.
 * (cost of upgrading to another Stripe Plan).
 *
 * @param {String} planId       - Plan ID to be upgraded to.
 * @return                      - Redux async function.
 */
export const getProrationCost = (planId: string) => (dispatch: AppDispatch) => {
  dispatch(gettingProrationCost(planId));
  axios
    .get(ucEndpointsUrls.stripeProrationCostPreview.replace(":planId", planId))
    .then(res =>
      dispatch(
        getProrationCostSuccess(res.data.cost, res.data.prorationDate, res.data.prorationToken)
      )
    )
    .catch(error => dispatch(getProrationCostFailed(getErrorMessageFromRequest(error))));
};

/**
 * Get current Penciler subscription.
 * (User's organization ID is read from token)
 *
 * @return {}           - Redux async function.
 */
export const getCurrentPencilerSubscription = () => (dispatch: AppDispatch) => {
  dispatch(gettingCurrentPencilerSubscription());
  axios
    .get(ucEndpointsUrls.currentPencilerSubscription)
    .then(res => dispatch(getCurrentPencilerSubscriptionSuccess(res.data)))
    .catch(error =>
      dispatch(getCurrentPencilerSubscriptionFailed(getErrorMessageFromRequest(error)))
    );
};
